import AxiosInstance from "@/utils/AxiosInstance";
import { splitIcdCode } from "@/utils/helpers";

const CHRONIC_CATEGORY = "Chronic Conditions";

export function isChronicConditionQuestion(q: any): boolean {
  if (!q) return false;
  const id = String(q.questionrandomid ?? "");
  if (id.includes("chronic")) return true;
  const cat = String(q.category ?? "").trim().toLowerCase();
  return cat === "chronic conditions" || cat === "chronic condition";
}

export function icdCodePartFromFull(icdCode: string): string {
  const s = String(icdCode ?? "").trim();
  if (!s) return "";
  if (s.includes(" - ")) return s.split(" - ")[0].trim();
  const [code] = splitIcdCode(s);
  if (code) return code;
  return s.split(/\s+/)[0];
}

/** Attach icdCode / icdCodePart / icdCodes when missing (backend often omits these). */
export function attachIcdFieldsToQuestion(q: any, icdCode: string): any {
  const full = String(icdCode ?? "").trim();
  const base = { ...q, category: q.category ?? CHRONIC_CATEGORY };
  if (!full) return base;
  const part = icdCodePartFromFull(full);
  return {
    ...base,
    icdCode:
      base.icdCode != null && String(base.icdCode).trim() !== ""
        ? base.icdCode
        : full,
    icdCodePart:
      base.icdCodePart != null && String(base.icdCodePart).trim() !== ""
        ? base.icdCodePart
        : part,
    icdCodes:
      Array.isArray(base.icdCodes) && base.icdCodes.length > 0
        ? base.icdCodes
        : [full],
  };
}

function questionsFromTemplate(template: any): any[] {
  if (Array.isArray(template?.questions)) return template.questions;
  if (Array.isArray(template?.sections)) {
    return template.sections.flatMap((s: any) =>
      Array.isArray(s?.questions) ? s.questions : [],
    );
  }
  return [];
}

function normalizeTemplateIcd(template: any): string[] {
  const raw = template?.icdCodes;
  if (Array.isArray(raw))
    return raw.filter((c: any) => c != null && c !== "").map(String);
  if (raw != null && raw !== "") return [String(raw)];
  const one = template?.icdCode;
  if (one != null && one !== "") return [String(one)];
  return [];
}

function unwrapApiRoot(payload: unknown): Record<string, unknown> {
  let root: unknown = payload;
  for (let i = 0; i < 3; i++) {
    if (
      root &&
      typeof root === "object" &&
      "data" in (root as object) &&
      (root as { data?: unknown }).data != null
    ) {
      root = (root as { data: unknown }).data;
    } else break;
  }
  return (root && typeof root === "object" ? root : {}) as Record<string, unknown>;
}

export function parseIcdQuestionnaireResponse(
  payload: unknown,
  selectedCodes: string[],
): any[] {
  const r = unwrapApiRoot(payload);
  const codes = selectedCodes.filter(Boolean).map(String);
  const fromApi = Array.isArray(r?.questions) ? (r.questions as any[]) : [];

  if (fromApi.length > 0) {
    return fromApi.map((q: any, idx: number) =>
      attachIcdFieldsToQuestion(
        {
          ...q,
          questionrandomid: q?.questionrandomid || `chronic_${idx + 1}`,
          category: CHRONIC_CATEGORY,
        },
        String(q?.icdCode ?? codes[0] ?? ""),
      ),
    );
  }

  const templates: any[] = Array.isArray(r?.templates)
    ? (r.templates as any[])
    : [];

  return templates.flatMap((template: any, tIdx: number) => {
    const fromTemplate = normalizeTemplateIcd(template);
    const icdCodes = fromTemplate.length ? fromTemplate : codes;
    const icdCode = icdCodes[0] ?? codes[tIdx] ?? codes[0] ?? "";
    return questionsFromTemplate(template).map((q: any, idx: number) =>
      attachIcdFieldsToQuestion(
        {
          ...q,
          questionrandomid: q?.questionrandomid || `chronic_${tIdx}_${idx + 1}`,
          category: CHRONIC_CATEGORY,
        },
        icdCode,
      ),
    );
  });
}

/** POST /icd-questionnaire/by-code — payload icdCode from care plan header ICD list */
export async function fetchChronicQuestionsFromIcdApi(
  icdCodes: string[],
): Promise<any[]> {
  const codes = (icdCodes ?? []).filter(
    (c) => c != null && String(c).trim() !== "",
  );
  if (!codes.length) return [];

  const { data } = await AxiosInstance().post("/icd-questionnaire/by-code", {
    icdCode: codes,
  });
  return parseIcdQuestionnaireResponse(data, codes);
}

function questionTextKey(q: any): string {
  return String(q?.question ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Saved care plan chronic rows (no icdCode from backend) + ICD questionnaire API rows → merged objects.
 */
export function mergeChronicQuestionsWithIcdApi(
  savedQuestions: any[],
  apiQuestions: any[],
  icdCodes: string[],
): any[] {
  const codes = icdCodes.filter(Boolean).map(String);
  const apiById = new Map<string, any>();
  const apiByText = new Map<string, any[]>();

  for (const aq of apiQuestions) {
    if (aq?.questionrandomid) {
      apiById.set(String(aq.questionrandomid), aq);
    }
    const tk = questionTextKey(aq);
    if (tk) {
      if (!apiByText.has(tk)) apiByText.set(tk, []);
      apiByText.get(tk)!.push(aq);
    }
  }

  const usedApiKeys = new Set<string>();

  return savedQuestions.map((sq, idx) => {
    const textKey = questionTextKey(sq);
    let ref: any | undefined;

    if (sq?.questionrandomid) {
      ref = apiById.get(String(sq.questionrandomid));
    }
    if (!ref && textKey) {
      const candidates = apiByText.get(textKey) ?? [];
      ref = candidates.find((c) => {
        const key = `${c.icdCodePart ?? ""}|${c.questionrandomid ?? ""}|${textKey}`;
        return !usedApiKeys.has(key);
      });
      if (ref) {
        usedApiKeys.add(
          `${ref.icdCodePart ?? ""}|${ref.questionrandomid ?? ""}|${textKey}`,
        );
      }
    }

    // Same order as API flat list when text/id match fails
    if (!ref && apiQuestions[idx]) {
      ref = apiQuestions[idx];
    }

    const icdFromRef = ref?.icdCode ?? ref?.icdCodes?.[0];
    const defaultCode =
      (icdFromRef && String(icdFromRef).trim()) ||
      codes[Math.min(idx, Math.max(codes.length - 1, 0))] ||
      codes[0] ||
      "";

    return attachIcdFieldsToQuestion(
      {
        ...(ref ?? {}),
        ...sq,
        answer: sq.answer,
        comment: sq.comment,
        options: sq.options?.length ? sq.options : ref?.options,
        type: sq.type ?? ref?.type,
        question: sq.question ?? ref?.question,
        questionrandomid:
          sq.questionrandomid ??
          ref?.questionrandomid ??
          `chronic_${idx + 1}`,
      },
      defaultCode,
    );
  });
}

export function mergeChronicQuestionsWithIcdApiAppendingNew(
  savedQuestions: any[],
  apiQuestions: any[],
  icdCodes: string[],
): any[] {
  console.log("mergeChronicQuestionsWithIcdApiAppendingNew");
  console.log("apiQuestions", apiQuestions);
  console.log("savedQuestions", savedQuestions);
  console.log('icdCodes', icdCodes);

  const codes = (icdCodes ?? []).filter(Boolean).map(String);
  const merged = [...(savedQuestions ?? [])];

  for (const aq of apiQuestions ?? []) {
    const que = String(aq?.question ?? "").trim();
    if (!que) continue;

    const aqPart = icdCodePartFromFull(
      String(aq?.icdCodePart ?? aq?.icdCode ?? ""),
    );
    const defaultCode =
      (aq?.icdCode && String(aq.icdCode).trim()) ||
      (aqPart &&
        codes.find((c) => icdCodePartFromFull(c) === aqPart)) ||
      codes[0] ||
      "";

    // Original behavior: find by question text only.
    const existingIndex = merged.findIndex(
      (q: any) => String(q?.question ?? "").trim() === que,
    );

    if (existingIndex >= 0) {
      const existing = merged[existingIndex];
      const existingPart = icdCodePartFromFull(
        String(existing?.icdCodePart ?? existing?.icdCode ?? ""),
      );

      // ONLY special case: same question text already mapped to a different ICD.
      // Keep both so each ICD section under Chronic Conditions shows the question.
      if (existingPart && aqPart && existingPart !== aqPart) {
        const alreadyHasThisIcd = merged.some(
          (q: any) =>
            String(q?.question ?? "").trim() === que &&
            icdCodePartFromFull(String(q?.icdCodePart ?? q?.icdCode ?? "")) ===
              aqPart,
        );
        if (!alreadyHasThisIcd) {
          merged.push(
            attachIcdFieldsToQuestion(
              {
                ...aq,
                questionrandomid:
                  aq?.questionrandomid ||
                  `chronic_${aqPart}_${merged.length + 1}`,
                category: aq?.category ?? CHRONIC_CATEGORY,
              },
              defaultCode,
            ),
          );
        }
        continue;
      }

      // All other cases — same as before: enrich existing row.
      merged[existingIndex] = attachIcdFieldsToQuestion(
        {
          ...merged[existingIndex],
          icdCode: aq?.icdCode,
          icdCodePart: aq?.icdCodePart,
          icdCodes: aq?.icdCodes,
        },
        defaultCode,
      );
      continue;
    }

    merged.push(
      attachIcdFieldsToQuestion(
        {
          ...aq,
          questionrandomid:
            aq?.questionrandomid || `chronic_${merged.length + 1}`,
          category: aq?.category ?? CHRONIC_CATEGORY,
        },
        defaultCode,
      ),
    );
  }
  console.log("merged--->", merged);
  return merged;
}

/** Care plan load: filter chronic rows, fetch by icdCodes, merge ICD fields at start */
export async function enrichCareplanChronicQuestions(
  careplanQuestions: any[],
  icdCodes: string[],
): Promise<any[]> {
  
  const codes = (icdCodes ?? []).filter(
    (c) => c != null && String(c).trim() !== "",
  );
  const apiQuestions = await fetchChronicQuestionsFromIcdApi(codes);
  // console.log('apiQuestions', apiQuestions);
  console.log('carePlanQuestions ----->', careplanQuestions);
  const saved = (careplanQuestions ?? []).filter(isChronicConditionQuestion);

  if (!saved.length) {
    return apiQuestions.length > 0 ? apiQuestions : [];
  }
  if (!codes.length) {
    // console.log("no codes");
    return saved.map((q, i) =>
      attachIcdFieldsToQuestion(q, codes[i] ?? ""),
    );
  }

  if (apiQuestions.length > 0) {
    // console.log("apiQuestions.length > 0");
    return mergeChronicQuestionsWithIcdApiAppendingNew(saved, apiQuestions, codes);
  }
  return saved.map((q, i) =>
    attachIcdFieldsToQuestion(q, codes[Math.min(i, codes.length - 1)] ?? codes[0]),
  );
}
